'use client';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  fetchMobileAcquisition,
  fetchMobileAcquisitionCaseName,
  fetchMobileAcquisitionPage,
  fetchMobileAcquisitionSummary,
  mobileKeys,
  type MobileAcquisitionRow,
  type MobileAcquisitionSummary,
  type MobileConnection,
} from '@/shared/mobile-service/api/cors-mobile-api';

/**
 * One page of acquisitions for the current tenant, over REST.
 *
 * REST reads `acquisition_map`; the GraphQL `acquisitions` query inner-joins
 * `case_info` and reads its nullable case columns, which both blanked the case
 * name and hid every acquisition lacking a `case_info` row. See
 * `MobileAcquisitionRow`.
 */
export function useAcquisitions(
  params: { offset?: number; limit?: number } = {},
): UseQueryResult<MobileConnection<MobileAcquisitionRow>, Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();
  const offset = params.offset ?? 0;
  const limit = params.limit ?? 100;

  return useQuery<MobileConnection<MobileAcquisitionRow>, Error>({
    queryKey: mobileKeys.acquisitionList(cellId, { offset, limit }),
    enabled,
    queryFn: ({ signal }) =>
      fetchMobileAcquisitionPage({
        cellId: cellId as string,
        accessToken: accessToken as string,
        offset,
        limit,
        signal,
      }),
  });
}

/** One acquisition's summary (type + per-artifact stats). */
export function useAcquisitionSummary(
  mobileAcquisitionId: number,
  active = true,
): UseQueryResult<MobileAcquisitionSummary | null, Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();

  return useQuery<MobileAcquisitionSummary | null, Error>({
    queryKey: mobileKeys.acquisitionSummary(cellId, mobileAcquisitionId),
    enabled: enabled && active && mobileAcquisitionId > 0,
    queryFn: ({ signal }) =>
      fetchMobileAcquisitionSummary({
        cellId: cellId as string,
        accessToken: accessToken as string,
        mobileAcquisitionId,
        signal,
      }),
  });
}

/**
 * The open acquisition's case name, from `acquisition_map` over REST.
 *
 * `useAcquisitionSummary` also carries a `caseName`, but it comes from the
 * nullable `case_info` join and so is null for most acquisitions — the same
 * reason the list reads REST. Anything that needs a real case name rather than a
 * label to display has to resolve it here; the assistant scopes chat history by
 * it. A case name never changes for an acquisition, hence the infinite
 * `staleTime`.
 */
export function useAcquisitionCaseName(
  mobileAcquisitionId: number,
): UseQueryResult<string | null, Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();

  return useQuery<string | null, Error>({
    queryKey: mobileKeys.acquisitionCaseName(cellId, mobileAcquisitionId),
    enabled,
    staleTime: Infinity,
    queryFn: ({ signal }) =>
      fetchMobileAcquisitionCaseName({
        cellId: cellId as string,
        accessToken: accessToken as string,
        mobileAcquisitionId,
        signal,
      }),
  });
}

/**
 * The open acquisition's ``acquisition_map`` row (case name and relative path).
 *
 * GraphQL ``caseName`` comes from nullable ``case_info`` and is usually empty.
 * The path and case name on this row do not change, hence the infinite
 * ``staleTime``.
 */
export function useAcquisitionMap(
  mobileAcquisitionId: number | null,
): UseQueryResult<MobileAcquisitionRow | null, Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();

  return useQuery<MobileAcquisitionRow | null, Error>({
    queryKey: mobileKeys.acquisitionMap(cellId, mobileAcquisitionId ?? 0),
    enabled: enabled && mobileAcquisitionId != null,
    staleTime: Infinity,
    queryFn: ({ signal }) =>
      fetchMobileAcquisition({
        cellId: cellId as string,
        accessToken: accessToken as string,
        mobileAcquisitionId: mobileAcquisitionId as number,
        signal,
      }),
  });
}
